/**
 * QUIL — OneDrive / Graph API operations
 *
 * Quil's path helpers over app-platform's canonical transport (`graphClient.ts`, work order W10).
 * The HTTP loop, retry policy, 401 handling, typed errors and the precondition contract all live in
 * the shared client now; this module only translates Quil's logical `/Apps/Quil/...` paths into
 * approot-relative ones and keeps the shapes sync.ts already speaks (`{ content, etag }`, delta items).
 *
 * The token getter is bound ONCE, by `connectGraph` (AuthProvider does it at module load), and is
 * asked for a token per attempt by the client. No function here takes a token: the first 401 forces
 * a refresh and retries once, a second throws `InteractiveSignInRequiredError`, which sync.ts already
 * treats as "pause, don't burn retries". Before W10 a 401 fell through as a generic Error.
 */

import { GRAPH_BASE, QUIL_ONEDRIVE_ROOT } from './auth'
import { getBoundAccountId } from './boundAccount'
import { reportServerTime, reportServerDate } from './clock'
import { InteractiveSignInRequiredError, type TokenGetter } from './authToken'
import {
  createGraphClient,
  forgetRememberedRoot,
  SIMPLE_UPLOAD_MAX,
  type GraphClient,
  type GraphClientConfig,
  type RequestOptions,
  type RequestPriority,
} from './graphClient'
import { isDocConflict } from './graphErrors'
import type { WritePrecondition } from './writePrecondition'

export { isDocConflict }

// SEC1: with the `Files.ReadWrite.AppFolder` scope the token can reach ONLY the app's
// dedicated special folder (`/me/drive/special/approot`, physically /Apps/Quil), and
// absolute `/root:/Apps/Quil/...` addressing is denied even for that same folder. So
// every Graph address is built relative to approot. The rest of the app still speaks in
// logical `/Apps/Quil/...` paths (QUIL_ONEDRIVE_ROOT); relPath strips that prefix.
const APP_ROOT = `${GRAPH_BASE}/me/drive/special/approot`

/** The approot's folder name. `verifyRoot()` fails closed unless Graph's approot is `/Apps/Quil`. */
const APP_FOLDER_NAME = 'Quil'

/** Sign-out: drop this account's remembered approot, so its next session checks before reading. */
export function forgetGraphRoot(accountId: string): void {
  forgetRememberedRoot(APP_FOLDER_NAME, accountId)
}

/** Strip the logical Quil root so a path becomes approot-relative ('' for the root itself). */
function relPath(path: string): string {
  const rel = path.startsWith(QUIL_ONEDRIVE_ROOT) ? path.slice(QUIL_ONEDRIVE_ROOT.length) : path
  return rel.replace(/^\/+/, '')
}

// ─── The client ────────────────────────────────────────────────────────────────

let tokenGetter: TokenGetter | null = null
let client: GraphClient | null = null

/**
 * The check EVERY OneDrive request passes before it is sent (W12 A5): "this store still belongs to
 * the account whose token is about to carry the request". Installed by `storeBinding` once a store
 * is bound; it throws `ScopeMismatchError` before a token is even acquired, so nothing leaves the
 * device when the store's owner stamp names someone else, and a read-only store refuses everything.
 * One place, at the transport, so no queued upload, queued DELETE, folder create, font read or
 * error-log write can slip past it by taking a path nobody remembered to guard.
 *
 * It runs from the token getter, not from `fetch`, on purpose: the transport treats anything thrown
 * by `fetch` as a network fault and retries it, which would turn a wrong-account refusal into four
 * attempts and a `GraphNetworkError`. A throw from the getter reaches the caller as itself.
 */
type RequestGuard = () => Promise<void>
let requestGuard: RequestGuard | null = null

export function setRequestGuard(guard: RequestGuard | null): void {
  requestGuard = guard
}
let overrides: Partial<Pick<GraphClientConfig, 'fetch' | 'sleep' | 'now' | 'random'>> = {}

/**
 * Bind the token getter every Graph call uses. It must honour `{ forceRefresh: true }` (the
 * canonical `createTokenGetter` does): that is what turns a first 401 into one silent retry.
 * `testOverrides` injects a fake fetch / instant sleep; production passes none.
 */
export function connectGraph(
  getToken: TokenGetter,
  testOverrides: Partial<Pick<GraphClientConfig, 'fetch' | 'sleep' | 'now' | 'random'>> = {},
): void {
  tokenGetter = getToken
  overrides = testOverrides
  client = null
}

function graph(): GraphClient {
  if (!client) {
    client = createGraphClient({
      // Read at call time, so a later connectGraph is honoured; unbound means signed out.
      getToken: async (o) => {
        if (requestGuard) await requestGuard()
        return tokenGetter ? tokenGetter(o) : Promise.reject(new InteractiveSignInRequiredError())
      },
      root: { kind: 'approot', expectFolderName: APP_FOLDER_NAME },
      // Every Graph response carries the server's current time. While this device has no precise
      // offset yet, that header is the only thing between a skewed device clock and
      // permanently-poisoned LWW stamps. A no-op once a precise anchor exists. See clock.ts.
      onServerDate: reportServerDate,
      // Load speed (hub brief 2026-10-14): once this account's approot has passed the check, later
      // sessions send their reads alongside the re-check instead of after it. Writes still wait.
      rememberRoot: { accountId: getBoundAccountId },
      ...overrides,
    })
  }
  return client
}

/** Address a sub-resource of an item (content / children / versions): approot/sub, or
 *  approot:/rel:/sub. Exported so the lazy version-history module can address `:/versions`
 *  without pulling its own Graph plumbing into the eager boot bundle. */
export function subUrl(path: string, sub: string): string {
  const rel = relPath(path)
  return rel ? `${graph().itemUrl(rel)}:/${sub}` : `${APP_ROOT}/${sub}`
}

/** One request through the shared client, after the approot check. For proseVersions. */
export async function graphRequest(url: string, init: RequestInit = {}, opts: RequestOptions = {}): Promise<Response> {
  // The client's own request() runs the fail-closed check: a write waits for it, a read may go
  // alongside it on a remembered approot (and is held until it passes).
  return graph().request(url, init, opts)
}

// ─── Public API ───────────────────────────────────────────────────────────────

/** Ensure /Quil/, /Quil/works/, /Quil/fonts/ and /Quil/backups/ exist. Call once after first auth.
 *  The fonts folder is the drop target for custom fonts (auto-loaded on launch).
 *  Folders are created by parent id (path-addressed POSTs under approot 400), cached per session. */
export async function ensureQuilFolders(): Promise<void> {
  await graph().ensureFolder('')
  await graph().ensureFolder('works')
  await graph().ensureFolder('fonts')
  await graph().ensureFolder('backups')   // the weekly library snapshots (W12 Phase B)
}

export interface FolderChild {
  name: string
  downloadUrl?: string   // pre-signed short-lived URL (Graph @microsoft.graph.downloadUrl)
  etag: string
}

/** List the files in a folder (folders skipped), following every page. Returns [] if the folder
 *  is missing (404) so callers can treat "no folder" and "empty folder" alike; a failure on a later
 *  page throws rather than returning a partial list. */
export async function listFolderChildren(
  path: string,
  opts: { priority?: RequestPriority } = {},
): Promise<FolderChild[]> {
  const items = await graph().listAll<Record<string, unknown>>(
    relPath(path), 'name,eTag,folder,@microsoft.graph.downloadUrl',
    opts.priority ? { priority: opts.priority } : {},
  )
  return items
    .filter(item => !item.folder)
    .map(item => ({
      name: String(item.name ?? ''),
      downloadUrl: (item['@microsoft.graph.downloadUrl'] as string | undefined) ?? undefined,
      etag: String(item.eTag ?? ''),
    }))
}

/** Ensure /Quil/works/{workId}/ and .../chapters/ exist before first upload. */
export async function ensureWorkFolders(workId: string): Promise<void> {
  await graph().ensureFolder(`works/${workId}`)
  await graph().ensureFolder(`works/${workId}/chapters`)
}

export interface RemoteFile {
  content: string
  /** The item eTag: Quil's content `If-Match` and its DELETE `If-Match` both name it. */
  etag: string
  /** The content tag (`cTag`), which survives a move or rename where the eTag does not (W11 live
   *  test 22). Used only to recognise a moved chapter as the version this device last wrote. */
  cTag?: string
}

/** Read file content + eTag (tag first, then bytes). Returns null if not found. Quil's
 *  preconditions name the item eTag, not the cTag (design §3.4's cTag choice is unverified). */
export async function readFileFromOneDrive(path: string): Promise<RemoteFile | null> {
  const read = await graph().readWithTag(relPath(path))
  if (!read) return null
  return { content: await read.response.text(), etag: read.eTag ?? '', cTag: read.tag }
}

/** What a write reports back. */
export interface WrittenFile {
  etag: string
  /** The content tag of what was written (see `RemoteFile.cTag`). */
  cTag?: string
  /**
   * `true` when an `If-Match` write was answered 201 Created: the file the tag named had gone
   * (deleted on another device, or its folder moved) and Graph made a NEW one with these bytes.
   * Real Graph does this rather than refusing (W10 §8.3, W11 live tests 19 and 22). Reported, never
   * thrown: sync.ts decides what that means (W11 §3.4).
   */
  recreated?: true
}

/**
 * Write file content under a precondition: an eTag string (`If-Match` it) or a full
 * `WritePrecondition`. A 412 (or a 409 on `expect-absent`) throws the canonical `DocConflictError`.
 * With none the write is unconditional: a never-synced chapter's first upload. Since W11 every
 * synced meta and aggregate write is conditional too (sync.ts).
 */
export async function writeFileToOneDrive(
  path: string,
  content: string,
  precondition?: string | WritePrecondition,
): Promise<WrittenFile> {
  const rel = relPath(path)
  const contentType = path.endsWith('.json') ? 'application/json' : 'text/plain'
  const pre: WritePrecondition = typeof precondition === 'string'
    ? (precondition ? { kind: 'if-match', tag: precondition } : { kind: 'none' })
    : (precondition ?? { kind: 'none' })
  const g = graph()

  if (new TextEncoder().encode(content).length > SIMPLE_UPLOAD_MAX) {
    // Past Graph's simple-upload ceiling: an upload session, each chunk retried. The client takes
    // a large body only as binary (a string over the limit is a RangeError), so it goes as a Blob.
    const written = await g.write(rel, new Blob([content], { type: contentType }), contentType, pre)
    return {
      etag: written.eTag ?? '',
      ...(written.tag ? { cTag: written.tag } : {}),
      ...(written.recreated ? { recreated: true as const } : {}),
    }
  }

  // The simple PUT goes through request() rather than write() for one reason: the written item's
  // lastModifiedDateTime, a millisecond-precise server "now" that anchors the shared clock every
  // synced entity stamp uses (lib/clock.ts). write() keeps only the tags.
  await g.verifyRoot()
  const headers = new Headers({ 'Content-Type': contentType })
  if (pre.kind === 'if-match') headers.set('If-Match', pre.tag)
  // The transport's own expect-absent form (graphClient `preconditionHeaders`): a 409 if it exists.
  const query = pre.kind === 'expect-absent' ? '?@microsoft.graph.conflictBehavior=fail' : ''
  const res = await g.request(g.contentUrl(rel) + query, { method: 'PUT', headers, body: content }, { conditional: { path: rel, pre } })
  const item = (await res.json().catch(() => ({}))) as { eTag?: unknown; cTag?: unknown; lastModifiedDateTime?: unknown }
  reportServerTime(typeof item.lastModifiedDateTime === 'string' ? item.lastModifiedDateTime : null)
  return {
    etag: typeof item.eTag === 'string' ? item.eTag : '',
    ...(typeof item.cTag === 'string' ? { cTag: item.cTag } : {}),
    // The transport's own rule (graphClient `wasRecreated`): an If-Match write answered 201.
    ...(pre.kind === 'if-match' && res.status === 201 ? { recreated: true as const } : {}),
  }
}

/**
 * Delete a file. A 404 (already gone) is success. With `ifMatchEtag` the delete is conditional
 * (W11 decision 2, settled 2026-10-07): a 412 throws `DocConflictError`, meaning another device
 * changed the file after this device last saw it, and the caller restores that edit.
 */
export async function deleteFileFromOneDrive(path: string, ifMatchEtag?: string): Promise<void> {
  await graph().remove(relPath(path), ifMatchEtag ? { kind: 'if-match', tag: ifMatchEtag } : undefined)
}

/** The names of the folders directly under `path` ([] when it is missing). For the W11
 *  check-moved rule: a work folder renamed outside Quil is found by its `meta.json` id. */
export async function listChildFolders(path: string): Promise<string[]> {
  const items = await graph().listAll<Record<string, unknown>>(relPath(path), 'name,folder')
  return items.filter(item => !!item.folder).map(item => String(item.name ?? ''))
}

// ─── Delta query ─────────────────────────────────────────────────────────────

export interface DeltaItem {
  id: string
  name: string
  parentReferencePath: string
  etag: string
  deleted: boolean
  lastModifiedDateTime: string | null
}

/**
 * Run the delta query against the approot (SEC1: the only tree the AppFolder-scoped token can
 * see). deltaToken is the @odata.deltaLink from the previous run (undefined for the first run).
 * Follows every nextLink; a 410 (expired token) restarts from scratch. Returns all changed items
 * plus the new token to store.
 */
export async function runDeltaQuery(deltaToken: string | undefined): Promise<{ items: DeltaItem[]; nextToken: string }> {
  const { items, deltaLink } = await graph().delta<Record<string, unknown>>(deltaToken)
  return {
    items: items.map(item => ({
      id: String(item.id ?? ''),
      name: String(item.name ?? ''),
      parentReferencePath: String(
        (item.parentReference as Record<string, unknown> | undefined)?.path ?? ''
      ),
      etag: String(item.eTag ?? ''),
      deleted: !!item.deleted,
      lastModifiedDateTime:
        typeof item.lastModifiedDateTime === 'string' ? item.lastModifiedDateTime : null,
    })),
    nextToken: deltaLink,
  }
}
